import { WebForm } from "screens/WebForms/WebFormsService";

/**
 * Shapes of "Build with AI" (the AI service's webform design sessions, passed
 * through core unchanged). Keys the service may add later are ignored, so
 * every optional key below can be missing on an older deployment.
 */

export type AiOutcome = "changed" | "unchanged" | "refused" | "kept";

export type AiVersionOrigin = "generated" | "ai_turn" | "restore";

export interface AiWarning {
  code: string;
  message: string;
  /** The field the warning is about (start warnings only), or null. */
  key?: string | null;
}

export interface AiChanges {
  page: boolean;
  pageTexts: string[];
  texts: string[];
  form: string[];
  theme: string[];
}

export interface AiFieldMapEntry {
  name: string;
  type: string;
  required: boolean;
  optionIds: (string | number)[] | null;
  page: number;
  dataLabel: string | null;
}

/** A design version. `html` and `fieldMap` are missing on an unchanged turn. */
export interface AiDesign {
  designId?: string;
  version: number;
  origin?: AiVersionOrigin;
  html?: string;
  fieldMap?: Record<string, AiFieldMapEntry>;
  sha256: string;
  size?: number;
  createdAt?: string;
  changed?: boolean;
  restoredFrom?: number | null;
  /** The turn that made it; design get leaves it out, the versions list has it. */
  turn?: number | null;
  /** Who made this version; only once the AI reports authorship. */
  byMe?: boolean;
  /** This version's own page words; only once the AI returns them on design get. */
  pageTexts?: string[];
  labelWarnings?: AiWarning[];
}

export interface AiStartResponse {
  sessionId: string;
  designId: string;
  turn: number;
  reply: string;
  design: AiDesign;
  changes: AiChanges;
  warnings: AiWarning[];
  createdAt: string;
}

export interface AiMessageResponse {
  sessionId: string;
  turn: number;
  reply: string;
  outcome: AiOutcome;
  changes: AiChanges;
  design: AiDesign;
  warnings: AiWarning[];
}

export interface AiSessionSummary {
  sessionId: string;
  designId: string;
  templateId: string;
  signerId: number;
  title: string;
  version: number;
  publishedVersion: number | null;
  turns: number;
  updatedAt: string;
}

export interface AiSessionList {
  sessions: AiSessionSummary[];
  nextCursor: string | null;
}

export interface AiHistoryMessage {
  turn: number;
  role: "user" | "assistant";
  text: string;
  designVersion: number | null;
  outcome: AiOutcome | null;
  at: string;
  byMe: boolean | null;
}

export interface AiSessionDetail {
  sessionId: string;
  designId: string;
  title: string;
  version: number;
  publishedVersion: number | null;
  createdAt: string;
  updatedAt: string;
  messages: AiHistoryMessage[];
  nextBefore: number | null;
}

export interface AiVersion {
  version: number;
  origin: AiVersionOrigin;
  turn: number | null;
  restoredFrom: number | null;
  sha256: string;
  size: number;
  createdAt: string;
  byMe?: boolean;
}

export interface AiVersionList {
  designId: string;
  versions: AiVersion[];
  currentVersion: number;
  publishedVersion: number | null;
}

export interface AiTemplateSummary {
  templateId: string;
  templateName: string;
  signerId: number;
  signerRole: string | null;
  fileName: string;
  pageCount?: number | null;
  fieldCount: number;
  /** Present when asked about a design: false once the template's fields changed. */
  fieldsMatch?: boolean;
}

export interface AiPublishRequest {
  version: number | null;
  sessionId: string;
  templateId: string;
  signerId: number;
  name: string | null;
  replaceExisting: boolean;
}

export interface AiPublishResponse {
  webForm: WebForm;
  publication: {
    publicationId: string;
    version: number;
    sha256: string;
    publishedAt: string;
  };
  archivedWebFormIds: string[];
}

/** What the page was opened with (query string and router state). */
export interface AiBuilderParams {
  sessionId: string | null;
  templateId: string;
  signerId: number;
  name: string;
  /** The opening style description from the build-choice dialog. */
  prompt?: string;
}

/** One line of the chat as the screen draws it, oldest first. */
export type ChatEntry =
  | {
      kind: "user";
      id: string;
      text: string;
      at: string | null;
      byMe: boolean | null;
      /** The opening line of a start: the document it was built from. */
      document?: { name: string; meta: string };
      /** The opening style, shown under "Create a webform for this document." */
      styleLine?: string;
    }
  | {
      kind: "assistant";
      id: string;
      text: string;
      at: string | null;
      outcome: AiOutcome | null;
      designVersion: number | null;
      turn: number | null;
      changes?: AiChanges;
      warnings?: AiWarning[];
      /** The message to put back in the box for "Try again" (a kept turn). */
      retryText?: string;
    }
  | { kind: "system"; id: string; text: string; at: string | null };

/** A chat message: 1 to 2000 characters. */
export const MAX_MESSAGE_LENGTH = 2000;
/** The opening style description. */
export const MAX_PROMPT_LENGTH = 1000;

export const isValidMessage = (text: string) =>
  text.trim().length > 0 && text.length <= MAX_MESSAGE_LENGTH;
